<template>
  <div class="group not-prose flex border border-shade-900 font-sans text-base">
    <div class="flex items-center border-r border-shade-900 bg-primary-50 px-4">
      <button
        v-if="!isPlaying"
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-400 transition-colors duration-200 ease-in-out hover:bg-primary-300 md:h-20 md:w-20"
        @click="play"
      >
        <IconPlay class="h-6 w-6 text-shade-50 md:h-12 md:w-12" />
      </button>
      <button
        v-else
        class="flex h-10 w-10 items-center justify-center rounded-full bg-primary-400 transition-colors duration-200 ease-in-out hover:bg-primary-300 md:h-20 md:w-20"
        @click="pause"
      >
        <IconPause class="h-6 w-6 text-shade-50 md:h-12 md:w-12" />
      </button>
    </div>
    <audio
      id="audioPlayer"
      preload="metadata"
      ref="audioPlayer"
      controls
      class="hidden"
      v-if="
        route.query?.onlyPlayer === 'true' ||
        route.path.includes('/embed/media/')
      "
      @timeupdate="onTimeUpdate"
    >
      <source :src="audio?.files?.[0]?.getUrl" />
    </audio>
    <div class="p-4 md:py-2 w-full">
      <a
        class="text-right underline text-primary-400"
        :href="`/media/${audio.slug}`"
        target="_blank"
        v-if="showLink"
      >
        <p>{{ $t('media.go_media') }}</p>
      </a>
      <h3
        class="text-mobile-lg font-semibold md:text-lg"
        v-if="locale !== 'fr' && props.audio.titleEn"
        v-html="props.audio.titleEn"
      />
      <h3
        class="text-mobile-lg font-semibold md:text-lg"
        v-else
        v-html="props.audio.title"
      />
      <p v-if="props.audio.work?.composers?.length > 0">
        {{
          $t('audio.description', {
            author:
              props.audio.work.composers[0].firstName +
              ' ' +
              props.audio.work.composers[0].lastName,
          })
        }}
        <span v-if="props.audio.timestamp"
          >,
          {{
            $t('audio.concert_on', {
              date: formatToFullDate(props.audio.timestamp),
            })
          }}</span
        >
      </p>
      <div class="flex items-center gap-4">
        <div class="mt-2 flex items-center gap-2">
          <p class="select-none text-mobile-lg md:text-ùd">
            {{ fmtMSSeconds(currentTime) }}
          </p>
          <input
            type="range"
            :value="currentTime"
            :max="duration"
            @change="updateCurrentTime"
            class="h-1 flex-1 appearance-none bg-primary-100 p-0 outline-none webkit-slider-thumb:h-6 webkit-slider-thumb:w-0.5 webkit-slider-thumb:appearance-none webkit-slider-thumb:border-none webkit-slider-thumb:bg-shade-50 webkit-slider-runnable-track:cursor-pointer webkit-slider-runnable-track:bg-transparent moz-range-track:bg-transparent moz-range-thumb:h-6 moz-range-thumb:w-0.5 moz-range-thumb:rounded-none moz-range-thumb:border-none moz-range-thumb:bg-shade-50 moz-range-progress:bg-transparent ms-track:bg-transparent ms-thumb:h-6 ms-thumb:w-0.5 ms-thumb:bg-shade-50 ms-tooltip:hidden ms-fill-lower:bg-transparent ms-fill-upper:bg-transparent"
          />
          <p class="select-none text-mobile-lg md:text-md">
            {{ fmtMSSeconds(duration) }}
          </p>
        </div>
        <button
          class="text-primary-400 opacity-0 transition-all duration-200 ease-in-out first-letter:uppercase hover:text-primary-100 group-hover:opacity-100"
          @click="addToPlaylist"
          v-if="
            route.query?.onlyPlayer !== 'true' ||
            route.path.includes('/embed/media/')
          "
        >
          {{ $t('audio.add_to_waiting_list') }}
        </button>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { PropType } from 'vue'
import IconPlay from '~/assets/icon/media/Play-Alt.svg'
import IconPause from '~/assets/icon/media/Pause.svg'
import { fmtMSSeconds } from '~/const/conversion'

const { $event } = useNuxtApp()
const { $listen } = useNuxtApp()

const { formatToFullDate } = useFullDateFormater()
const route = useRoute()
const audioPlayer = ref(null)
const { locale } = useI18n()

const props = defineProps({
  audio: {
    type: Object as PropType<Media>,
    required: true,
  },
  showLink: {
    type: Boolean,
    default: false,
  },
})
let isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)

useState('audio', () => props.audio)

// Emitter
const play = () => {
  isPlaying.value = true
  if (
    route.query?.onlyPlayer === 'true' ||
    route.path.includes('/embed/media/')
  ) {
    audioPlayer.value.play()
  } else {
    $event('audio', {
      type: 'play',
      media: {
        ...props.audio.files[0],
        title: props.audio.title,
        titleEn: props.audio.titleEn,
      },
      id: props.audio.id,
    })
  }
}
const addToPlaylist = () => {
  $event('audio', {
    type: 'add_to_playlist',
    media: props.audio,
  })
}
const pause = () => {
  if (
    route.query?.onlyPlayer === 'true' ||
    route.path.includes('/embed/media/')
  ) {
    isPlaying.value = false
    audioPlayer.value.pause()
  } else {
    $event('audio', {
      type: 'pause',
      media: props.audio,
    })
  }
}

const onTimeUpdate = () => {
  currentTime.value = Math.round(audioPlayer.value.currentTime)
}

const updateCurrentTime = (event) => {
  currentTime.value = event.target.value
  audioPlayer.value.currentTime = currentTime.value
}

onMounted(() => {
  if (
    route.query?.onlyPlayer === 'true' ||
    route.path.includes('/embed/media/')
  ) {
    duration.value = Math.round(audioPlayer.value.duration)
  }
})
watch(currentTime, (newTime, oldValue) => {
  if (parseInt(oldValue) + 1 !== parseInt(newTime)) {
    $event('durationSecondPlayer', parseInt(newTime))
  }
})

// Listener
$listen('duration', (data) => {
  currentTime.value = Math.round(data.currentTime)
  duration.value = Math.round(data.duration)
})

$listen('pause', () => {
  isPlaying.value = false
})

$listen('play', (data) => {
  if (props.audio.files[0].getUrl === data.value) {
    isPlaying.value = true
  }
})
</script>
